import { logger } from '@jurnee/common/src/browser/Logger';
import { PrimaryButton } from '@jurnee/common/src/components/primitives/buttons/PrimaryButton';
import { SecondaryButton } from '@jurnee/common/src/components/primitives/buttons/SecondaryButton';
import { Drawer } from '@jurnee/common/src/components/primitives/Drawer';
import { useToast } from '@jurnee/common/src/components/primitives/hooks/useToast';
import { HStack } from '@jurnee/common/src/components/primitives/stack/HStack';
import { PayoutBeneficiaryJSON } from '@jurnee/common/src/entities/PayoutBeneficiary';
import type { AirwallexBeneficiary, BeneficiaryParams } from '@jurnee/common/src/services/AirwallexService/types';
import { createPayoutBeneficiary, getProviderBeneficiary, updatePayoutBeneficiary } from 'api/payoutBeneficiaries';
import { cloneElement, HTMLAttributes, ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getPartnerSelector } from 'store/partner/partner.selectors';
import { AirwallexBeneficiaryForm, AirwallexBeneficiaryFormRef } from './AirwallexBeneficiaryForm';

interface Props {
  payoutBeneficiary?: PayoutBeneficiaryJSON;
  children: ReactElement<HTMLAttributes<HTMLElement>>;
  defaultIsOpen?: boolean;
  onUpsert(payoutBeneficiary: PayoutBeneficiaryJSON): void;
}

export function PayoutInformationDrawer(props: Props) {
  const { t } = useTranslation(['payoutInformation', 'common']);
  const toast = useToast();
  const partner = useSelector(getPartnerSelector);

  const formRef = useRef<AirwallexBeneficiaryFormRef>(null);
  const [isOpen, setIsOpen] = useState(!!props.defaultIsOpen);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingBeneficiary, setIsFetchingBeneficiary] = useState(false);
  const [providerBeneficiary, setProviderBeneficiary] = useState<AirwallexBeneficiary | null>(null);
  const hasBeneficiaryForm = isOpen && !isFetchingBeneficiary;

  async function fetchProviderBeneficiary() {
    if (!partner || !props.payoutBeneficiary) {
      return;
    }
    try {
      setIsFetchingBeneficiary(true);
      const providerBeneficiary = await getProviderBeneficiary(partner.id, props.payoutBeneficiary.id);
      setProviderBeneficiary(providerBeneficiary);
    } catch (error: unknown) {
      logger.error('Failed to fetch provider beneficiary:', { error });
      toast.error(t('toasts.fetchBeneficiary.error'));
      onClose();
    } finally {
      setIsFetchingBeneficiary(false);
    }
  }

  useEffect(() => {
    if (isOpen && props.payoutBeneficiary) {
      fetchProviderBeneficiary();
    }
  }, [isOpen, props.payoutBeneficiary, partner]);

  function onOpen(event: React.MouseEvent) {
    event.stopPropagation();
    setIsOpen(true);
  }

  function onClose() {
    setIsOpen(false);
  };

  async function upsertBeneficiary(values: BeneficiaryParams) {
    if (!partner) {
      throw new Error('Partner not found');
    }
    if (props.payoutBeneficiary) {
      return updatePayoutBeneficiary(partner.id, props.payoutBeneficiary.id, values);
    }

    return createPayoutBeneficiary(partner.id, values);
  }

  async function handleSubmit() {
    if (!formRef.current) {
      return;
    }
    try {
      setIsLoading(true);

      const { values, errors } = await formRef.current.submit();

      if (errors) {
        setIsLoading(false);
        return;
      }

      const payoutBeneficiary = await upsertBeneficiary(values as BeneficiaryParams);

      props.onUpsert(payoutBeneficiary);
      toast.success(t(`toasts.upsertBeneficiary.success`));
      onClose();
    } catch (error: unknown) {
      logger.error('Failed to upsert provider beneficiary:', { error });
      toast.error(t(`toasts.upsertBeneficiary.error`));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      { cloneElement(props.children, { onClick: onOpen }) }
      <Drawer
        size='md'
        isOpen={isOpen}
        onClose={onClose}
        title={t('title')}
        p={2}
        footer={
          <HStack w="100%" justifyContent="space-between">
            <SecondaryButton size="sm" colorScheme="gray" onClick={onClose} mr={2}>
              {t('common:buttons.close')}
            </SecondaryButton>
            <PrimaryButton size="sm" colorScheme="black" onClick={handleSubmit} isDisabled={isLoading}>
              {t('common:buttons.save')}
            </PrimaryButton>
          </HStack>
        }
      >
        {hasBeneficiaryForm && partner && (
          <AirwallexBeneficiaryForm
            ref={formRef}
            partnerId={partner.id}
            beneficiary={providerBeneficiary ?? undefined}
            onReady={() => setIsLoading(false)}
            onLoadingChange={setIsLoading}
            onError={() => setIsLoading(false)}
          />
        )}
      </Drawer>
    </>
  );
}